import { isEmpty } from 'lib';
import { getRequestTimeDuration } from 'utils';

const COUNT_RANGE_CUSTOM_MODE = 'custom';

export const DIGIKALA_SEARCH_API_PATHS = {
  COMMENTS: '/digikala/comments',
  COMMENTS_COUNT: '/digikala/comments/count',
  BRANDS: '/digikala/brands/competitors',
  CATEGORIES: '/digikala/categories',
  PRODUCTS: '/digikala/products',
  SELLERS: '/digikala/sellers',
};

export const DIGIKALA_SEARCH_ENDPOINT = '/search/api/v1/search';

export const DIGIKALA_DEFAULT_SORT = 'time';
export const DIGIKALA_DEFAULT_SORT_TYPE = 'desc';

const STAR_RANGE_TO_RATES = {
  '1-2': [1, 2],
  '2-3': [2, 3],
  '3-4': [3, 4],
  '4-5': [4, 5],
};

const appendCountRange = (params, value, minKey, maxKey) => {
  if (value?.mode !== COUNT_RANGE_CUSTOM_MODE || isEmpty(value.from)) {
    return;
  }

  params[minKey] = Number(value.from);

  if (!isEmpty(value.to)) {
    params[maxKey] = Number(value.to);
  }
};

const expandStarRates = (selectedIds = []) => {
  const rates = new Set();

  selectedIds.forEach((selectedId) => {
    const mappedRates = STAR_RANGE_TO_RATES[selectedId];

    if (mappedRates) {
      mappedRates.forEach((rate) => rates.add(rate));
      return;
    }

    const numericRate = Number(selectedId);

    if (!Number.isNaN(numericRate)) {
      rates.add(numericRate);
    }
  });

  return [...rates];
};

/**
 * Brands in scope when the brand filter is untouched: the user's assigned
 * brands, or the single seed brand when no assigned list is loaded.
 */
export const getDefaultBrandCodes = (seedBrandCode, seedBrandCodes = []) => {
  if (Array.isArray(seedBrandCodes) && !isEmpty(seedBrandCodes)) {
    return seedBrandCodes;
  }

  return isEmpty(seedBrandCode) ? [] : [seedBrandCode];
};

/**
 * Resolve brand codes for comments/filter lookups. The brand filter holds the
 * full selection, so the user's own brands can be unchecked; an untouched
 * (empty) filter falls back to the default brands.
 */
export const resolveBrandCodes = (
  appliedFilters = {},
  seedBrandCode,
  seedBrandCodes = []
) => {
  if (Array.isArray(appliedFilters.brand) && !isEmpty(appliedFilters.brand)) {
    return appliedFilters.brand;
  }

  return getDefaultBrandCodes(seedBrandCode, seedBrandCodes);
};

/**
 * Same time handling as BrandBaan/content search:
 * preset duration → numeric time_duration (seconds);
 * custom range → from/to and no time_duration.
 */
export const appendTimeRange = (params, { timeDuration, from, to }) => {
  if (!isEmpty(timeDuration)) {
    params.from = undefined;
    params.to = undefined;
    params.time_duration = getRequestTimeDuration({ timeDuration });
  } else {
    params.time_duration = undefined;
    params.from = from;
    params.to = to;
  }
};

const toNumericIds = (ids) => ids.map(Number).filter((id) => !Number.isNaN(id));

/**
 * Lookup endpoints return the ids in `selected_ids` at the head of page 1
 * (flagged `is_selected`), so applied filter values never get buried in the list.
 */
export const appendSelectedIds = (params, selectedIds) => {
  if (Array.isArray(selectedIds) && !isEmpty(selectedIds)) {
    params.selected_ids = selectedIds;
  }
};

/**
 * Build query params for /digikala/comments and /digikala/comments/count.
 */
export const buildDigikalaCommentsParams = ({
  appliedFilters = {},
  seedBrandCode,
  seedBrandCodes = [],
  q,
  timeDuration,
  from,
  to,
  page,
  count,
  sort = DIGIKALA_DEFAULT_SORT,
  sortType = DIGIKALA_DEFAULT_SORT_TYPE,
} = {}) => {
  const params = {
    sort,
    sort_type: sortType,
    page,
    count,
  };

  if (!isEmpty(q) && String(q).trim().length >= 2) {
    params.q = String(q).trim();
    params.full_text = 1;
  }

  appendTimeRange(params, { timeDuration, from, to });

  const brandCodes = resolveBrandCodes(
    appliedFilters,
    seedBrandCode,
    seedBrandCodes
  );

  /**
   * Do not suffix keys with []. apiGet uses qs.stringify which already
   * serializes arrays (e.g. resource_product_id[0]=...). Adding [] caused
   * resource_product_id[][] and 422 "must be a number".
   */
  if (!isEmpty(brandCodes)) {
    params.resource_brand_code = brandCodes;
  }

  if (
    Array.isArray(appliedFilters.category) &&
    !isEmpty(appliedFilters.category)
  ) {
    params.resource_category = appliedFilters.category;
  }

  if (
    Array.isArray(appliedFilters.product) &&
    !isEmpty(appliedFilters.product)
  ) {
    params.resource_product_id = toNumericIds(appliedFilters.product);
  }

  if (Array.isArray(appliedFilters.seller) && !isEmpty(appliedFilters.seller)) {
    params.resource_seller_id = toNumericIds(appliedFilters.seller);
  }

  if (
    Array.isArray(appliedFilters.userRate) &&
    !isEmpty(appliedFilters.userRate)
  ) {
    const rates = expandStarRates(appliedFilters.userRate);

    if (!isEmpty(rates)) {
      params.rate = rates;
    }
  }

  appendCountRange(params, appliedFilters.like, 'min_likes', 'max_likes');
  appendCountRange(
    params,
    appliedFilters.dislike,
    'min_dislikes',
    'max_dislikes'
  );

  const hasScopeFilter =
    !isEmpty(brandCodes) ||
    !isEmpty(appliedFilters.category) ||
    !isEmpty(appliedFilters.product) ||
    !isEmpty(appliedFilters.seller);

  if (hasScopeFilter) {
    appendCountRange(
      params,
      appliedFilters.raters,
      'min_reviewers',
      'max_reviewers'
    );
  }

  return params;
};

export const buildDigikalaLookupParams = ({
  seedBrandCode,
  seedBrandCodes = [],
  brandCodes,
  categories,
  productIds,
  sellerIds,
  selectedIds,
  q,
  timeDuration,
  from,
  to,
  page,
} = {}) => {
  const params = {};

  appendTimeRange(params, { timeDuration, from, to });
  appendSelectedIds(params, selectedIds);

  if (page != null) {
    params.page = page;
  }

  if (!isEmpty(q) && String(q).trim().length >= 2) {
    params.q = String(q).trim();
    params.full_text = 1;
  }

  const resolvedBrandCodes = resolveBrandCodes(
    { brand: brandCodes },
    seedBrandCode,
    seedBrandCodes
  );

  if (!isEmpty(resolvedBrandCodes)) {
    params.resource_brand_code = resolvedBrandCodes;
  }

  if (!isEmpty(categories)) {
    params.resource_category = categories;
  }

  if (!isEmpty(productIds)) {
    params.resource_product_id = toNumericIds(productIds);
  }

  if (!isEmpty(sellerIds)) {
    params.resource_seller_id = toNumericIds(sellerIds);
  }

  return params;
};

export const extractSearchList = (response) =>
  response?.data?.result?.data || [];

export const extractSearchCount = (response) => {
  const data = response?.data?.result?.data;

  if (typeof data === 'number') {
    return data;
  }

  if (typeof data?.count === 'number') {
    return data.count;
  }

  const metaTotal = response?.data?.result?.meta?.total;

  if (typeof metaTotal === 'number') {
    return metaTotal;
  }

  return null;
};
